import { mediaCache } from './media-cache';
import { graphqlClient } from './graphql/client';
import { PageMediaResponse } from './graphql/schema';

// Cache for page media responses to avoid duplicate GraphQL calls
const pageMediaCache = new Map<string, { data: PageMediaResponse; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Preload all media for a page by slug using GraphQL
 * Returns the media URLs that were preloaded
 */
export async function preloadPageMedia(slug: string): Promise<string[]> {
  const normalizedSlug = slug === '/' ? '' : slug.replace(/^\//, '');
  
  try {
    // Check cache first
    const cached = pageMediaCache.get(normalizedSlug);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      // Use cached data - just trigger preloading
      return triggerPreloadFromPageMedia(cached.data);
    }

    // Fetch page media via GraphQL (single optimized query)
    const response = await graphqlClient.queryGet<{ pageMediaOptimized: PageMediaResponse }>(
      'pageMediaOptimized',
      { pageSlug: normalizedSlug || '/' }
    );

    const pageMedia = response?.pageMediaOptimized;
    if (!pageMedia) {
      return [];
    }

    // Cache the response
    pageMediaCache.set(normalizedSlug, { data: pageMedia, timestamp: Date.now() });

    // Trigger preloading based on priority
    return triggerPreloadFromPageMedia(pageMedia);
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[preloadPageMedia] Error preloading media for page ${normalizedSlug}:`, error);
    }
    return [];
  }
}

/**
 * Trigger preloading from PageMediaResponse
 * CRITICAL: Checks cache first to avoid reloading media that's already cached
 * This ensures returning to pages doesn't require reloading media files
 */
function triggerPreloadFromPageMedia(pageMedia: PageMediaResponse): string[] {
  const allUrls: string[] = [];

  // Extract URLs from categorized media
  // Use CDN URLs when available (CloudFront now configured with proper CORS headers)
  const criticalUrls = pageMedia.criticalMedia.map((m) => m.url);
  const highPriorityUrls = pageMedia.highPriorityMedia.map((m) => m.url);
  const normalUrls = pageMedia.normalMedia.map((m) => m.url);
  const lowPriorityUrls = pageMedia.lowPriorityMedia.map((m) => m.url);

  allUrls.push(...criticalUrls, ...highPriorityUrls, ...normalUrls, ...lowPriorityUrls);

  // CRITICAL: Filter out URLs that are already cached/preloaded
  // This prevents reloading media when returning to pages
  const filterCached = async (urls: string[]): Promise<string[]> => {
    const uncached: string[] = [];
    for (const url of urls) {
      const isCached = await mediaCache.isInBrowserCache(url);
      if (!isCached && !mediaCache.isPreloaded(url)) {
        uncached.push(url);
      }
    }
    return uncached;
  };

  // Preload critical media immediately with high priority (only if not cached)
  if (criticalUrls.length > 0) {
    filterCached(criticalUrls).then(uncached => {
      if (uncached.length > 0) {
        mediaCache.preloadBatch(uncached, 'high').catch(() => {
      // Silently handle errors
        });
      }
    });
  }

  // Preload high priority media with high priority (only if not cached)
  if (highPriorityUrls.length > 0) {
    filterCached(highPriorityUrls).then(uncached => {
      if (uncached.length > 0) {
        mediaCache.preloadBatch(uncached, 'high').catch(() => {
      // Silently handle errors
        });
      }
    });
  }

  // Queue normal and low priority media for background loading (only if not cached)
  const backgroundUrls = [...normalUrls, ...lowPriorityUrls];
  if (backgroundUrls.length > 0) {
    filterCached(backgroundUrls).then(uncached => {
      if (uncached.length > 0) {
        mediaCache.addToQueue(uncached);
    mediaCache.processQueue('low').catch(() => {
      // Silently handle errors
        });
      }
    });
  }

  return allUrls;
}

/**
 * Preload media for multiple pages (for navigation prefetching)
 */
export async function preloadMultiplePagesMedia(slugs: string[]): Promise<void> {
  // Process pages sequentially to avoid overwhelming the network
  for (const slug of slugs) {
    await preloadPageMedia(slug);
    // Small delay between pages
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
}

/**
 * Get cached page media (synchronous check)
 */
export function getCachedPageMedia(slug: string): PageMediaResponse | null {
  const normalizedSlug = slug === '/' ? '' : slug.replace(/^\//, '');
  const cached = pageMediaCache.get(normalizedSlug);
  
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  
  return null;
}

/**
 * Clear page media cache
 */
export function clearPageMediaCache(): void {
  pageMediaCache.clear();
}

/**
 * Wait for high priority media to start loading (with timeout)
 */
export async function waitForMediaPreload(urls: string[], timeoutMs = 500): Promise<void> {
  if (urls.length === 0) {
    return Promise.resolve();
  }

  // Wait a short time for high priority items to start loading
  // We don't wait for completion, just for the preload to begin
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, timeoutMs);
  });
}

